import React, { useEffect, useState } from "react";
import { CheckIcon, SearchIcon } from "common-lib";
import { InspModelItem, RuleItem } from "@types";
import ModelDetailModal from "./ModelDetailModal";
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  Collapse,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import { Stack } from "@mui/system";
import {
  DeleteOutline,
  EditOutlined,
  Link,
  LinkOff,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  deleteInspModel,
  getPagedInspModelList,
} from "../../services/modelService";
import { CustomPagination } from "../customComponent/CustomPagination";
import { t } from "i18next";
import { formatDate } from "../../utils/utils";

interface ModelCardProps {
  ruleList: RuleItem[];
  setRuleList: (rule: RuleItem[]) => void;
  selectedModelId?: number | null;
  onModelsChanged?: () => void; // 수정/삭제 후 부모의 전체 모델 목록(규칙 탭용) 갱신
}

const PAGE_SIZE = 20;

export function ModelCard({
  ruleList,
  setRuleList,
  selectedModelId,
  onModelsChanged,
}: ModelCardProps) {
  const [modelList, setModelList] = useState<InspModelItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [query, setQuery] = useState("");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<{
    key: "name" | "create_at";
    dir: "asc" | "desc";
  }>({
    key: "create_at",
    dir: "desc",
  });
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState<InspModelItem | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openInEdit, setOpenInEdit] = useState(false);

  // 검색/정렬/페이지는 서버에서 처리
  const fetchModels = async () => {
    try {
      const res = await getPagedInspModelList({
        page,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        sort_type: sort.key,
        sort: sort.dir,
      });
      // 마지막 페이지의 마지막 항목을 지운 경우 이전 페이지로
      if (res.contents.length === 0 && page > 1) {
        setPage(page - 1);
        return;
      }
      setModelList(res.contents);
      setTotalCount(res.total_count);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [page, keyword, sort]);

  // 검색어 입력 debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setKeyword(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSort = (key: "name" | "create_at") => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const headSx = (width: string) => ({
    fontSize: 11,
    fontWeight: 500,
    color: "text.secondary",
    py: 1.25,
    width,
    bgcolor: "grey.50",
  });

  const getRuleCount = (modelId: number) =>
    ruleList.filter((r) => r.inspModel.id === modelId).length;

  const onModelSelect = (id: number, edit = false) => {
    const model = modelList.find((m) => m.id === id);

    if (!model) return;

    setSelectedModel(model);
    setOpenInEdit(edit);
    setIsModalOpen(true);
  };

  const handleUpdated = (updated: InspModelItem) => {
    setModelList(modelList.map((m) => (m.id === updated.id ? updated : m)));
    setSelectedModel(updated);
    onModelsChanged?.();
  };

  const handleDeleted = (id: number) => {
    handleCloseModal();
    fetchModels();
    onModelsChanged?.();
  };

  const handleDeleteClick = async (
    e: React.MouseEvent,
    model: InspModelItem,
  ) => {
    e.stopPropagation();
    if (getRuleCount(model.id) > 0) {
      toast.warn(
        `연결된 검사규칙 ${getRuleCount(model.id)}개를 먼저 삭제해주세요.`,
      );
      return;
    }
    if (!window.confirm(`'${model.name}' ${t("DeleteConfirmMsg")}`)) return;
    try {
      await deleteInspModel(model.id);
      handleDeleted(model.id);
      toast.success("검사모델이 삭제되었습니다.");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedModel(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">{t("LoadingModelsMsg")}</p>
      </div>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
        <TextField
          size="small"
          placeholder={t("ModelSearchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 260,
            "& .MuiOutlinedInput-root": {
              fontSize: 12,
              borderRadius: 2,
              bgcolor: "grey.50",
              "& fieldset": { borderColor: "grey.300" },
            },
          }}
        />
        <Typography fontSize={12} color="text.secondary">
          {`${totalCount}${t("ItemCountUnit")}`}
        </Typography>
      </Stack>

      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: "hidden", borderColor: "grey.200" }}
      >
        <TableContainer sx={{ maxHeight: 480, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={headSx("28%")}>
                  <TableSortLabel
                    active={sort.key === "name"}
                    direction={sort.key === "name" ? sort.dir : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    {t("InspModelName")}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headSx("28%")}>{t("Description")}</TableCell>
                <TableCell sx={headSx("12%")}>{t("UsedRules")}</TableCell>
                <TableCell sx={headSx("22%")}>
                  <TableSortLabel
                    active={sort.key === "create_at"}
                    direction={sort.key === "create_at" ? sort.dir : "asc"}
                    onClick={() => handleSort("create_at")}
                  >
                    {t("CreateDate")}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headSx("10%")} />
              </TableRow>
            </TableHead>

            <TableBody>
              {modelList.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      textAlign: "center",
                      py: 5,
                      color: "text.secondary",
                      fontSize: 13,
                    }}
                  >
                    {t("NoModelMsg")}
                  </TableCell>
                </TableRow>
              ) : (
                modelList.map((model) => {
                  const ruleCount = getRuleCount(model.id);
                  const isSelected = selectedModelId === model.id;

                  return (
                    <TableRow
                      key={model.id}
                      onClick={() => onModelSelect(model.id)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: isSelected ? "primary.50" : "transparent",
                        "&:hover": {
                          bgcolor: isSelected ? "primary.50" : "grey.50",
                        },
                        "& td": {
                          borderBottom: "0.5px solid",
                          borderColor: "grey.200",
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize={13} fontWeight={500}>
                          {model.name}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1.25 }}>
                        <Typography
                          fontSize={12}
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 240,
                          }}
                        >
                          {model.description || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1.25 }}>
                        <Chip
                          size="small"
                          icon={
                            ruleCount > 0 ? (
                              <Link sx={{ fontSize: "12px !important" }} />
                            ) : (
                              <LinkOff sx={{ fontSize: "12px !important" }} />
                            )
                          }
                          label={`${ruleCount}${t("RuleCountUnit")}`}
                          sx={{
                            fontSize: 11,
                            height: 22,
                            bgcolor: ruleCount > 0 ? "primary.50" : "grey.100",
                            color:
                              ruleCount > 0 ? "primary.800" : "text.secondary",
                            border: "none",
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          fontSize: 12,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDate(model.create_at)}
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 0.5,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={t("Edit")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onModelSelect(model.id, true);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: "#007AFF" },
                            }}
                          >
                            <EditOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("Delete")}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, model)}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: "#ff3b30" },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <CustomPagination
          page={page}
          totalPages={Math.ceil(totalCount / PAGE_SIZE)}
          onPageChange={setPage}
          groupSize={5}
        />
      </Box>

      {selectedModel && (
        <ModelDetailModal
          model={selectedModel}
          ruleList={ruleList}
          setRuleList={setRuleList}
          open={isModalOpen}
          onClose={handleCloseModal}
          initialEdit={openInEdit}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}
    </Box>
  );
}
